import { useEffect, useState, useRef, useCallback } from 'react'
import { useAuth } from '@/lib/config/auth-provider'
import { useRouter } from '@/routing/navigation'
import { onDraftStatusChanged, onExperimentStatusChanged } from '@/lib/services/websocket'
import Link from '@/routing/Link'
import dynamic from '@/routing/dynamic'
import type { DeleteStudyDialogProps } from '@/types/components/delete-study-dialog'
import { PageContent } from '@/components/layout/PageContent'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageLayout } from '@/components/layout/PageLayout'
import { useStudiesLobbyPaginated } from '@/lib/hooks/use-studies-lobby-paginated'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Input } from '@/components/ui/input'
import {
  Plus,
  Loader2,
  Search,
  X,
  ChevronDown,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
} from 'lucide-react'
import { StudyLobbyCard } from './components/StudyLobbyCard'
import { LobbyEmptyState } from '@/components/shared/LobbyEmptyState'
import { LockedFreeGate, useIsLockedFree } from '@/components/billing/LockedFreeGate'
import { cn } from '@/lib/utils'
import { experimentsApi } from '@/lib/api'
import {
  archiveQuestionnaireDraftApi,
  duplicateQuestionnaireDraftApi,
} from '@/features/studies/questionnaire/api/questionnaireDraftsApi'
import { toast } from 'sonner'
import { AnalyticsEvents, track } from '@/lib/services/analytics'
import type {
  StudiesLobbyItem,
  StudiesLobbySortBy,
  StudiesLobbySortDirection,
} from '@/types/routes/tests'
import { getDraftDisplayName } from '../draftDisplay'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import {
  STUDY_LIST_TABLE_GRID_CLASS,
  STUDY_LIST_TABLE_HEADER_CLASS,
} from './studyListTableLayout'

const SEARCH_DEBOUNCE_MS = 300


const DeleteStudyDialog = dynamic<DeleteStudyDialogProps>(
  () =>
    import('./components/DeleteStudyDialog').then((mod) => ({
      default: mod.DeleteStudyDialog,
    })),
)

function stripFixturePrefix(value?: string) {
  if (!value) return ''
  const stripped = value.replace(/^\s*\[fixture[^\]]*]\s*/i, '').trim()
  return stripped || value.trim()
}

function getExperimentName(item: Extract<StudiesLobbyItem, { itemKind: 'experiment' }>) {
  const cleanedName = stripFixturePrefix(item.name)
  if (cleanedName) return cleanedName

  const reportHeadline =
    typeof item.reportHeadline === 'string' ? item.reportHeadline.trim() : ''
  if (reportHeadline) return reportHeadline

  if (item.templateId) {
    return item.templateId
      .split('-')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }
  return i18n.t('studies.untitled', { ns: 'app' })
}

function getItemDisplayName(item: StudiesLobbyItem | null): string {
  if (!item) return i18n.t('studies.untitled', { ns: 'app' })
  if (item.itemKind === 'draft') return getDraftDisplayName(item)
  return getExperimentName(item)
}

export default function TestsPage() {
  const { t } = useTranslation('app')
  const { user } = useAuth()
  const isLockedFree = useIsLockedFree()
  const router = useRouter()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<StudiesLobbyItem | null>(null)
  // Default expanded so drafts are immediately visible as the last section.
  const [allExpanded, setAllExpanded] = useState(true)
  const [draftsExpanded, setDraftsExpanded] = useState(true)
  const [sortBy, setSortBy] = useState<StudiesLobbySortBy>('date')
  const [sortDirection, setSortDirection] = useState<StudiesLobbySortDirection>('desc')
  const [draftSortBy, setDraftSortBy] = useState<StudiesLobbySortBy>('date')
  const [draftSortDirection, setDraftSortDirection] = useState<StudiesLobbySortDirection>('desc')
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim())
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [searchQuery])

  const {
    items,
    isLoading: loading,
    isLoadingMore,
    hasMore,
    loadMore,
    reset: resetLobby,
    mutate,
    removeItem,
  } = useStudiesLobbyPaginated(6, 5, debouncedSearch, sortBy, sortDirection)
  const {
    items: recentItems,
    reset: resetRecentLobby,
    mutate: mutateRecentLobby,
    removeItem: removeRecentItem,
  } = useStudiesLobbyPaginated(12, 12, debouncedSearch)

  const studiesListTrackedRef = useRef(false)

  const hasSearch = debouncedSearch.length > 0
  const showToolbar = items.length > 0 || searchQuery.trim().length > 0

  useEffect(() => {
    if (loading || studiesListTrackedRef.current) return
    studiesListTrackedRef.current = true
    track(AnalyticsEvents.STUDIES_LIST_VIEWED)
  }, [loading])

  useEffect(() => {
    if (searchOpen) {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus()
      })
    }
  }, [searchOpen])

  useEffect(() => {
    const user = localStorage.getItem('user')
    if (!user) {
      router.push('/login')
      return
    }

    try {
      const userStr = localStorage.getItem('user')
      if (userStr) {
        const u = JSON.parse(userStr)
        setIsPlatformAdmin(u.isPlatformAdmin === true)
      }
    } catch (e) {
      console.error('Failed to parse user from localStorage:', e)
    }
  }, [router])

  useEffect(() => {
    return onExperimentStatusChanged(() => {
      resetLobby()
      resetRecentLobby()
    })
  }, [resetLobby, resetRecentLobby])

  useEffect(() => {
    return onDraftStatusChanged(() => {
      resetLobby()
      resetRecentLobby()
    })
  }, [resetLobby, resetRecentLobby])

  const handleOpen = useCallback(
    (item: StudiesLobbyItem) => {
      if (item.itemKind === 'draft') {
        if (item.status === 'duplicating') return
        router.push(`/tests/new?draftId=${item.id}`)
        return
      }
      router.push(`/tests/${item.id}/results`)
    },
    [router],
  )

  // One copy per click: a second click while a duplicate request is in
  // flight makes no second copy (B18).
  const duplicatingRef = useRef(false)
  const handleDuplicate = useCallback(
    async (item: StudiesLobbyItem) => {
      if (duplicatingRef.current) return
      duplicatingRef.current = true
      try {
        let draftId: string
        let sourcePopulationId: string | undefined
        if (item.itemKind === 'draft') {
          const result = await duplicateQuestionnaireDraftApi(item.id)
          draftId = result.draftId
          sourcePopulationId = result.sourcePopulationId
        } else {
          const result = await experimentsApi.duplicate(item.id)
          draftId = result.draftId
          sourcePopulationId = result.sourcePopulationId
        }
        const audienceParam = sourcePopulationId ? `&audience=${sourcePopulationId}` : ''
        router.push(`/tests/new?draftId=${draftId}&from=duplicate${audienceParam}`)
      } catch {
        toast.error(t('studyLobby.duplicateFailed'))
      } finally {
        duplicatingRef.current = false
      }
    },
    [router],
  )

  const deleteItem = useCallback(async (item: StudiesLobbyItem) => {
    try {
      setDeletingId(item.id)
      if (item.itemKind === 'draft') {
        await archiveQuestionnaireDraftApi(item.id)
        removeItem('draft', item.id)
        removeRecentItem('draft', item.id)
      } else {
        await experimentsApi.delete(item.id)
        track(AnalyticsEvents.EXPERIMENT_DELETED, {
          experiment_id: item.id,
        })
        removeItem('experiment', item.id)
        removeRecentItem('experiment', item.id)
      }
      mutate()
      mutateRecentLobby()
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    } catch (error) {
      console.error('Failed to remove study item:', error)
      toast.error(
        item.itemKind === 'draft'
          ? t('studyLobby.archiveFailed')
          : t('studyLobby.deleteFailed'),
      )
    } finally {
      setDeletingId(null)
    }
  }, [mutate, mutateRecentLobby, removeItem, removeRecentItem])

  const handleRequestDelete = useCallback((item: StudiesLobbyItem) => {
    const isFailed =
      item.itemKind === 'experiment' &&
      (item.status === 'failed' || item.status === 'cancelled')

    // Drafts already confirmed inline on the row action; failed studies skip confirm.
    if (isFailed || item.itemKind === 'draft') {
      void deleteItem(item)
      return
    }

    setItemToDelete(item)
    setDeleteDialogOpen(true)
  }, [deleteItem])

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return
    await deleteItem(itemToDelete)
  }

  const handleSort = (nextSortBy: StudiesLobbySortBy) => {
    if (nextSortBy === sortBy) {
      setSortDirection((currentDirection) =>
        currentDirection === 'asc' ? 'desc' : 'asc',
      )
      return
    }

    setSortBy(nextSortBy)
    setSortDirection('asc')
  }

  const renderSortHeader = (label: string, column: StudiesLobbySortBy) => {
    const isActive = sortBy === column
    const sortLabel = isActive
      ? t(sortDirection === 'asc' ? 'studyLobby.sortedAsc' : 'studyLobby.sortedDesc', { label })
      : t('studyLobby.sortBy', { label })

    return (
      <button
        type="button"
        data-testid={`studies-sort-${column}`}
        aria-label={sortLabel}
        aria-pressed={isActive}
        onClick={() => handleSort(column)}
        className="inline-flex items-center gap-1 text-left transition-colors hover:text-foreground"
      >
        {label}
        {isActive ? (
          sortDirection === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ArrowDown className="h-3.5 w-3.5" aria-hidden />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5" aria-hidden />
        )}
      </button>
    )
  }

  const handleDraftSort = (nextSortBy: StudiesLobbySortBy) => {
    if (nextSortBy === draftSortBy) {
      setDraftSortDirection((currentDirection) =>
        currentDirection === 'asc' ? 'desc' : 'asc',
      )
      return
    }

    setDraftSortBy(nextSortBy)
    setDraftSortDirection('asc')
  }

  const renderDraftSortHeader = (label: string, column: StudiesLobbySortBy) => {
    const isActive = draftSortBy === column
    const sortLabel = isActive
      ? t(draftSortDirection === 'asc' ? 'studyLobby.sortedAsc' : 'studyLobby.sortedDesc', { label })
      : t('studyLobby.sortDraftsBy', { label })

    return (
      <button
        type="button"
        data-testid={`drafts-sort-${column}`}
        aria-label={sortLabel}
        aria-pressed={isActive}
        onClick={() => handleDraftSort(column)}
        className="inline-flex items-center gap-1 text-left transition-colors hover:text-foreground"
      >
        {label}
        {isActive ? (
          draftSortDirection === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ArrowDown className="h-3.5 w-3.5" aria-hidden />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5" aria-hidden />
        )}
      </button>
    )
  }

  if (loading && items.length === 0 && !hasSearch) {
    return (
      <div className="min-h-screen bg-background">
        <PageLayout className="max-w-none p-8 lg:p-8">
          <PageHeader
            title={(
              <Heading level={1} className="text-2xl md:text-4xl">
                {t('studies.title')}
              </Heading>
            )}
            description={t('studies.description')}
            action={(
              <div className="flex flex-wrap items-center gap-2 md:gap-3">
                <div className="h-10 w-[88px] animate-pulse rounded-md bg-muted" />
                <div className="h-10 w-10 animate-pulse rounded-lg bg-muted" />
                <div className="h-10 w-[160px] animate-pulse rounded-lg bg-muted" />
              </div>
            )}
          />
          <PageContent className="gap-0">
            <div className="overflow-x-auto">
              <div className="min-w-[1000px]">
                <div className={STUDY_LIST_TABLE_HEADER_CLASS}>
                  <div className="min-w-0 font-sans">{t('studies.colStudy')}</div>
                  <div>{t('studies.colType')}</div>
                  <div>{t('studies.colRespondents')}</div>
                  <div>{t('studies.colCreatedBy')}</div>
                  <div>{t('studies.colDate')}</div>
                  <div />
                </div>
                <div className="[&>*:last-child]:border-b-0">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <div
                      key={i}
                      className={`${STUDY_LIST_TABLE_GRID_CLASS} border-b border-border/70 py-3`}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="h-[60px] w-[80px] shrink-0 animate-pulse rounded-md bg-muted" />
                        <div className="min-w-0 flex-1 space-y-2">
                          <div
                            className="h-4 animate-pulse rounded bg-muted"
                            style={{ width: `${40 + (i * 13) % 45}%` }}
                          />
                          <div className="h-3 w-24 animate-pulse rounded bg-muted/60" />
                        </div>
                      </div>
                      <div>
                        <div className="h-5 w-24 animate-pulse rounded-full bg-muted/60" />
                      </div>
                      <div>
                        <div className="h-3 w-28 animate-pulse rounded bg-muted/60" />
                      </div>
                      <div>
                        <div className="h-3 w-8 animate-pulse rounded bg-muted/60" />
                      </div>
                      <div>
                        <div className="h-3 w-20 animate-pulse rounded bg-muted/60" />
                      </div>
                      <div />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </PageContent>
        </PageLayout>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <PageLayout className="max-w-none p-8 lg:p-8">
        <PageHeader
          title={(
            <Heading level={1} className="text-2xl md:text-4xl">
              {t('studies.title')}
            </Heading>
          )}
          description={t('studies.description')}
          action={(
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              {showToolbar && (
                <>
                  <div className="relative h-10 w-10">
                    <div
                      className={cn(
                        'flex h-10 items-center overflow-hidden rounded-lg border-[1.5px] border-cta bg-background transition-all duration-200',
                        searchOpen || searchQuery ? 'absolute right-0 top-0 z-10 w-[280px] px-2' : 'w-10 px-0',
                      )}
                    >
                      <button
                        type="button"
                        aria-label={t('studies.searchAria')}
                        onClick={() => setSearchOpen(true)}
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center text-cta"
                      >
                        <Search className="h-4 w-4 -translate-x-px -translate-y-px" />
                      </button>
                      <Input
                        ref={searchInputRef}
                        type="text"
                        placeholder={t('studies.searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={cn(
                          'h-10 flex-1 border-0 bg-transparent px-0 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 font-sans',
                          !(searchOpen || searchQuery) && 'pointer-events-none opacity-0',
                        )}
                      />
                      <button
                        type="button"
                        aria-label={t('studies.searchClear')}
                        onClick={() => {
                          setSearchQuery('')
                          setSearchOpen(false)
                        }}
                        className={cn(
                          'inline-flex h-10 w-8 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground',
                          !(searchOpen || searchQuery) && 'pointer-events-none opacity-0',
                        )}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </>
              )}
              {!isLockedFree && (
                <Link href="/tests/new" className="w-full md:w-auto">
                  <Button variant="cta" className="w-full md:w-auto h-10 rounded-lg px-4 text-base font-medium justify-center">
                    <Plus className="mr-2 h-4 w-4" />
                    {t('studies.create')}
                  </Button>
                </Link>
              )}
            </div>
          )}
        />

        <PageContent className="gap-0">
          {loading && items.length === 0 && hasSearch ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 && hasSearch ? (
            <div className="py-12 text-center">
              <Text color="muted" className="font-sans">
                {t('studies.noMatch', { query: searchQuery })}
              </Text>
            </div>
          ) : items.length === 0 && isLockedFree ? (
            <LockedFreeGate action={t('studies.createAction')} ctaLabel={t('studies.upgradeCreate')} />
          ) : items.length === 0 ? (
            <LobbyEmptyState
              title={t('studies.emptyTitle')}
              description={t('studies.emptyBody')}
            >
              <Button asChild variant="cta" className="rounded-[6px] h-10 px-4 text-base">
                <Link href="/tests/new">
                  <Plus className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} />
                  {t('studies.createFirst')}
                </Link>
              </Button>
            </LobbyEmptyState>
          ) : (
            <>
              {(() => {
                const draftItems = items.filter((item) => item.itemKind === 'draft')
                const otherItems = items.filter((item) => item.itemKind !== 'draft')
                const recentStudyItems = recentItems.filter(
                  (item) => item.itemKind === 'experiment',
                )
                const sortedDraftItems = [...draftItems].sort((a, b) => {
                  let comparison = 0
                  if (draftSortBy === 'study') {
                    comparison = getDraftDisplayName(a).localeCompare(
                      getDraftDisplayName(b),
                      undefined,
                      { sensitivity: 'base' },
                    )
                  } else if (draftSortBy === 'type') {
                    comparison = (a.goalId ?? 'Custom').localeCompare(
                      b.goalId ?? 'Custom',
                      undefined,
                      { sensitivity: 'base' },
                    )
                  } else if (draftSortBy === 'date') {
                    comparison = new Date(a.lastActivityAt).getTime() - new Date(b.lastActivityAt).getTime()
                  }

                  if (comparison !== 0) {
                    return draftSortDirection === 'asc' ? comparison : -comparison
                  }
                  return a.id.localeCompare(b.id)
                })

                function renderCard(item: StudiesLobbyItem, variant: 'grid' | 'list') {
                  const displayName = getItemDisplayName(item)
                  const updatedDate = new Date(item.lastActivityAt).toLocaleDateString(
                    'en-US',
                    { month: 'short', day: 'numeric', year: 'numeric' },
                  )
                  const navigationDisabled =
                    item.itemKind === 'experiment' &&
                    (item.status === 'failed' || item.status === 'cancelled')
                  return (
                    <StudyLobbyCard
                      key={`${item.itemKind}:${item.id}`}
                      variant={variant}
                      item={item}
                      studyName={displayName}
                      updatedLabel={updatedDate}
                      navigationDisabled={navigationDisabled}
                      isPlatformAdmin={isPlatformAdmin}
                      currentUserId={user?.id}
                      onOpen={() => handleOpen(item)}
                      onRequestDelete={() => handleRequestDelete(item)}
                      onDuplicate={() => handleDuplicate(item)}
                    />
                  )
                }

                function renderDraftsSection() {
                  if (draftItems.length === 0) {
                    return null
                  }

                  const draftsHeader = (
                    <div className="flex w-full items-center border-b border-border/70 px-4 py-2.5">
                      <button
                        type="button"
                        data-testid="drafts-section-toggle"
                        onClick={() => setDraftsExpanded((prev) => !prev)}
                        className="flex items-center gap-2 text-sm font-normal text-muted-foreground transition-colors hover:text-foreground"
                      >
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform duration-200',
                            draftsExpanded ? 'rotate-0' : '-rotate-90',
                          )}
                        />
                        {t('studies.drafts')}
                        <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                          {draftItems.length}
                        </span>
                      </button>
                    </div>
                  )

                  const draftContent = !draftsExpanded ? null : (
                    <>
                      <div className={STUDY_LIST_TABLE_HEADER_CLASS}>
                        <div className="min-w-0 font-sans">{renderDraftSortHeader(t('studies.colStudy'), 'study')}</div>
                        <div>{renderDraftSortHeader(t('studies.colType'), 'type')}</div>
                        <div>{t('studies.colRespondents')}</div>
                        <div>{t('studies.colCreatedBy')}</div>
                        <div>{renderDraftSortHeader(t('studies.colDate'), 'date')}</div>
                        <div />
                      </div>
                      <div className="[&>*:last-child]:border-b-0">
                        {sortedDraftItems.map((item) => renderCard(item, 'list'))}
                      </div>
                    </>
                  )

                  return (
                    <div className="overflow-hidden rounded-xl border border-border/70">
                      {draftsHeader}
                      {draftsExpanded ? (
                        <div className="overflow-x-auto">
                          <div className="min-w-[1000px] [&>*:last-child]:border-b-0">{draftContent}</div>
                        </div>
                      ) : draftContent}
                    </div>
                  )
                }

                return (
                  <div className="flex flex-col gap-8">
                    {recentStudyItems.length > 0 && (
                      <section aria-labelledby="recent-studies-heading" className="flex flex-col gap-4">
                        <Text id="recent-studies-heading" className="font-sans text-sm font-medium text-muted-foreground">
                          {t('studies.recent')}
                        </Text>
                        <div
                          className="relative z-10 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-8 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                          data-testid="recent-studies-rail"
                        >
                          {recentStudyItems.map((item) => (
                            <div
                              key={`recent:${item.id}`}
                              className="w-full shrink-0 snap-start sm:w-[calc((100%-1rem)/2)] xl:w-[calc((100%-2rem)/3)]"
                            >
                              {renderCard(item, 'grid')}
                            </div>
                          ))}
                        </div>
                      </section>
                    )}

                    <div className="flex flex-col gap-4">
                      <section aria-labelledby="all-studies-heading" className="overflow-hidden rounded-xl border border-border/70">
                        <button
                          type="button"
                          data-testid="all-studies-section-toggle"
                          id="all-studies-heading"
                          onClick={() => setAllExpanded((prev) => !prev)}
                          className="flex w-full items-center gap-2 border-b border-border/70 px-4 py-2.5 text-sm font-normal text-muted-foreground transition-colors hover:text-foreground"
                        >
                          <ChevronDown
                            className={cn(
                              'h-4 w-4 transition-transform duration-200',
                              allExpanded ? 'rotate-0' : '-rotate-90',
                            )}
                          />
                          {t('studies.all')}
                          <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                            {otherItems.length}
                          </span>
                        </button>
                        {allExpanded && (
                          <div className="overflow-x-auto">
                            <div className="min-w-[1000px]">
                              <div className={STUDY_LIST_TABLE_HEADER_CLASS}>
                                <div className="min-w-0 font-sans">{renderSortHeader(t('studies.colStudy'), 'study')}</div>
                                <div>{renderSortHeader(t('studies.colType'), 'type')}</div>
                                <div>{renderSortHeader(t('studies.colRespondents'), 'respondents')}</div>
                                <div>{renderSortHeader(t('studies.colCreatedBy'), 'creator')}</div>
                                <div>{renderSortHeader(t('studies.colDate'), 'date')}</div>
                                <div />
                              </div>
                              <div className="[&>*:last-child]:border-b-0">
                                {otherItems.map((item) => renderCard(item, 'list'))}
                              </div>
                              {hasMore && (
                                <div className="flex justify-center py-4">
                                  <button
                                    type="button"
                                    data-testid="load-more-studies-button"
                                    onClick={loadMore}
                                    disabled={isLoadingMore}
                                    className="flex items-center gap-1.5 text-base font-medium text-foreground hover:text-primary transition-colors disabled:opacity-50"
                                  >
                                    {isLoadingMore ? (
                                      <>
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        {t('studies.loading')}
                                      </>
                                    ) : (
                                      <>
                                        {t('studies.loadMore')}
                                        <Plus className="h-3.5 w-3.5" />
                                      </>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </section>
                      {renderDraftsSection()}
                    </div>
                  </div>
                )
              })()}
            </>
          )}

          <DeleteStudyDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            studyName={getItemDisplayName(itemToDelete)}
            mode={itemToDelete?.itemKind === 'draft' ? 'archive' : 'delete'}
            deleting={deletingId !== null}
            onCancel={() => {
              setDeleteDialogOpen(false)
              setItemToDelete(null)
            }}
            onConfirm={handleConfirmDelete}
          />

        </PageContent>
      </PageLayout>
    </div>
  )
}
